import type { PayloadAction } from '@reduxjs/toolkit'

import { groupReaching } from 'api/canonicalPicker'
import type { PickerRow } from 'api/canonicalPicker.types'

import type {
  CanonicalLevel,
  CanonicalLevelState,
  OperatorOption,
  PickerSliceState,
  SourceSelection,
} from './types'
import {
  createInitialCanonicalLevel,
  ensurePicker,
  getGroupConfigForPicker,
  getNextPickerId,
} from './types'

const EMPTY_SELECTION: SourceSelection = { ids: [], rows: {} }

// thanx-ai's level-2 "any": it opens no level-2 field
export const SKIP_OPERATOR = 'skip'

/**
 * The level-2 operator a card starts on: the payload's while it preselects
 * level-2 values, else "any", so a card never opens on an empty field to fill.
 */
export const startingLevel2Operator = (
  options: OperatorOption[],
  preselected: string | undefined,
  hasValues: boolean
): string => {
  const operator = preselected ?? options[0]?.id ?? SKIP_OPERATOR
  if (hasValues) return operator
  const requiresValues =
    options.find(option => option.id === operator)?.requiresValues !== false
  const canSkip = options.some(option => option.id === SKIP_OPERATOR)
  return requiresValues && canSkip ? SKIP_OPERATOR : operator
}
const EMPTY_LEVEL: CanonicalLevelState = createInitialCanonicalLevel()

// Ids come from the rows, not the object's keys, which list numeric ids in ascending order
const selectionOf = (rows: PickerRow[]): SourceSelection => ({
  ids: Array.from(new Set(rows.map(row => row.id))),
  rows: Object.fromEntries(rows.map(row => [row.id, row])),
})

export const picksOf = (selection: SourceSelection): PickerRow[] =>
  selection.ids.map(id => selection.rows[id])

/** The canonical product a picked row stands for; an unmatched uid stands for none */
export const canonicalIdOf = (row: PickerRow): string | null => {
  switch (row.kind) {
    case 'product':
      return row.id
    case 'uidGroup':
    case 'storeUid':
      return row.canonicalId
    case 'unmatchedUid':
      return null
  }
}

/** Every canonical product a picked row reaches: a version, each its group's variants belong to */
const productIdsOf = (row: PickerRow): string[] => {
  if (row.kind === 'storeUid') return row.productIds
  if (row.kind === 'uidGroup' && row.productIds) return row.productIds
  const canonicalId = canonicalIdOf(row)
  return canonicalId === null ? [] : [canonicalId]
}

/** The canonical ids a level's picks reach, which scope the level below */
export const scopeOf = (selection: SourceSelection): string[] =>
  Array.from(new Set(picksOf(selection).flatMap(productIdsOf)))

const activeSourceOf = (
  state: PickerSliceState,
  pickerId: number,
  level: CanonicalLevel
): string | null =>
  state.pickers[pickerId]?.canonical?.[level].source ??
  getGroupConfigForPicker(state, pickerId)?.[level].defaultSource ??
  null

const activeSelectionOf = (
  state: PickerSliceState,
  pickerId: number,
  level: CanonicalLevel
): SourceSelection => {
  const source = activeSourceOf(state, pickerId, level)
  const levelState = state.pickers[pickerId]?.canonical?.[level]
  return (source && levelState?.bySource[source]) || EMPTY_SELECTION
}

/**
 * Applies a change to one level. Level 2 is picked among the children of
 * level 1's products, so it starts over when level 1 loses one of them.
 */
const updateLevel = (
  state: PickerSliceState,
  pickerId: number,
  level: CanonicalLevel,
  update: (levelState: CanonicalLevelState) => void
) => {
  const picker = state.pickers[pickerId]
  if (!picker) return
  if (!picker.canonical) {
    picker.canonical = {
      level1: createInitialCanonicalLevel(),
      level2: createInitialCanonicalLevel(),
    }
  }
  const { canonical } = picker
  if (level === 'level2') {
    update(canonical.level2)
    return
  }

  const scopeBefore = scopeOf(activeSelectionOf(state, pickerId, 'level1'))
  update(canonical.level1)
  const scopeAfter = new Set(
    scopeOf(activeSelectionOf(state, pickerId, 'level1'))
  )
  if (scopeBefore.some(id => !scopeAfter.has(id))) {
    canonical.level2 = createInitialCanonicalLevel()
  }
}

interface LevelPayload {
  pickerId: number
  level: CanonicalLevel
}

export const canonicalReducers = {
  /** Adds an empty card, its level 2 on "any" */
  createCanonicalPicker: (
    state: PickerSliceState,
    action: PayloadAction<{ groupId: string }>
  ) => {
    const { groupId } = action.payload
    const picker = ensurePicker(state, getNextPickerId(state), groupId)
    picker.bottom.operator = startingLevel2Operator(
      state.groups[groupId]?.config.level2.operatorOptions ?? [],
      undefined,
      false
    )
  },

  /** Replaces what is picked from one of the level's sources */
  setCanonicalPicks: (
    state: PickerSliceState,
    action: PayloadAction<LevelPayload & { source: string; rows: PickerRow[] }>
  ) => {
    const { pickerId, level, source, rows } = action.payload
    updateLevel(state, pickerId, level, levelState => {
      levelState.bySource[source] = selectionOf(rows)
    })
  },

  toggleCanonicalPick: (
    state: PickerSliceState,
    action: PayloadAction<LevelPayload & { source: string; row: PickerRow }>
  ) => {
    const { pickerId, level, source, row } = action.payload
    updateLevel(state, pickerId, level, levelState => {
      const selection = levelState.bySource[source] ?? selectionOf([])
      if (selection.rows[row.id]) {
        selection.ids = selection.ids.filter(id => id !== row.id)
        delete selection.rows[row.id]
      } else {
        selection.ids.push(row.id)
        selection.rows[row.id] = row
      }
      levelState.bySource[source] = selection
    })
  },

  /** Fills in what a source says about picks the level holds only by id; the ids it left out stay unnamed */
  describeCanonicalPicks: (
    state: PickerSliceState,
    action: PayloadAction<
      LevelPayload & { source: string; ids: string[]; rows: PickerRow[] }
    >
  ) => {
    const { pickerId, level, source, ids, rows } = action.payload
    const levelState = state.pickers[pickerId]?.canonical?.[level]
    const selection = levelState?.bySource[source]
    if (!levelState || !selection) return
    const named = new Set(rows.map(row => row.id))
    levelState.unnamedIds = ids.filter(
      id => selection.rows[id] && !named.has(id)
    )
    rows.forEach(row => {
      const picked = selection.rows[row.id]
      if (picked?.kind !== 'product' || row.kind !== 'product') return
      selection.rows[row.id] = {
        ...picked,
        name: row.name,
        variants: picked.variants ?? row.variants,
      }
    })
  },

  /** Remembers the picks the source couldn't name, so the merchant can ask again */
  failToDescribeCanonicalPicks: (
    state: PickerSliceState,
    action: PayloadAction<LevelPayload & { source: string; ids: string[] }>
  ) => {
    const { pickerId, level, source, ids } = action.payload
    updateLevel(state, pickerId, level, levelState => {
      const picked = levelState.bySource[source]?.rows ?? {}
      levelState.unnamedIds = ids.filter(id => picked[id])
    })
  },

  /** Records every product picked versions' groups belong to; level 2 only gains scope */
  scopeCanonicalPicks: (
    state: PickerSliceState,
    action: PayloadAction<
      LevelPayload & { source: string; productIds: Record<string, string[]> }
    >
  ) => {
    const { pickerId, level, source, productIds } = action.payload
    const levelState = state.pickers[pickerId]?.canonical?.[level]
    const selection = levelState?.bySource[source]
    if (!levelState || !selection) return
    const scoped = new Set(Object.keys(productIds))
    levelState.unscopedIds = levelState.unscopedIds.filter(
      id => !scoped.has(id)
    )
    Object.entries(productIds).forEach(([id, ids]) => {
      const picked = selection.rows[id]
      if (picked?.kind === 'uidGroup') {
        selection.rows[id] = groupReaching(picked, ids)
      }
    })
  },

  /** Remembers the versions whose products couldn't be looked up, so the merchant can ask again */
  failToScopeCanonicalPicks: (
    state: PickerSliceState,
    action: PayloadAction<LevelPayload & { source: string; ids: string[] }>
  ) => {
    const { pickerId, level, source, ids } = action.payload
    updateLevel(state, pickerId, level, levelState => {
      const picked = levelState.bySource[source]?.rows ?? {}
      levelState.unscopedIds = Array.from(
        new Set([...levelState.unscopedIds, ...ids])
      ).filter(id => picked[id])
    })
  },

  /** Moves the level to another source, starting it with the given picks */
  switchCanonicalSource: (
    state: PickerSliceState,
    action: PayloadAction<LevelPayload & { source: string; rows: PickerRow[] }>
  ) => {
    const { pickerId, level, source, rows } = action.payload
    updateLevel(state, pickerId, level, levelState => {
      levelState.source = source
      levelState.bySource[source] = selectionOf(rows)
    })
  },

  /** Moves the level back to its default source, discarding the other's picks */
  switchToDefaultSource: (
    state: PickerSliceState,
    action: PayloadAction<LevelPayload>
  ) => {
    const { pickerId, level } = action.payload
    updateLevel(state, pickerId, level, levelState => {
      if (levelState.source) delete levelState.bySource[levelState.source]
      levelState.source = null
    })
  },
}

type RootState = { picker: PickerSliceState }

export const selectCanonicalLevel = (
  state: RootState,
  pickerId: number,
  level: CanonicalLevel
): CanonicalLevelState =>
  state.picker.pickers[pickerId]?.canonical?.[level] ?? EMPTY_LEVEL

/** The name of the source a level reads from right now */
export const selectActiveSource = (
  state: RootState,
  pickerId: number,
  level: CanonicalLevel
): string | null => activeSourceOf(state.picker, pickerId, level)

/** What is picked from the source a level reads from; other sources' picks don't count */
export const selectActiveSelection = (
  state: RootState,
  pickerId: number,
  level: CanonicalLevel
): SourceSelection => activeSelectionOf(state.picker, pickerId, level)

export const selectHasAnyCanonicalPick = (
  state: RootState,
  groupId: string
): boolean =>
  Object.entries(state.picker.pickerToGroup).some(
    ([pickerId, pickerGroupId]) =>
      pickerGroupId === groupId &&
      activeSelectionOf(state.picker, Number(pickerId), 'level1').ids.length > 0
  )
